/**
 * The two anonymous public writes (canon `contracts/API.md` § Public writes).
 *
 * Each builds exactly the documented body and nothing else. Core refuses unknown fields
 * rather than ignoring them, so these functions are the single place where a request shape
 * is assembled — a component passes values, never a raw body.
 *
 * Nothing here decides an outcome. Whether an address was already subscribed and whether a
 * deal may be reported are both core's decisions; the site renders the answer.
 *
 * Deal submission used to be the third. Canon decision `D-008` put it behind a seller
 * session, so it is no longer an anonymous public write and no longer something this bundle
 * can send at all — `docs/SECURITY.md` § Public mutations records the move, and `WEB-014`
 * removed the caller.
 */
import { postJson } from "./client";
import type {
  DealReportRequest,
  DealReportResponse,
  SubscriptionRequest,
  SubscriptionResponse,
} from "./types";

export function subscribe(
  request: SubscriptionRequest,
  signal?: AbortSignal,
): Promise<SubscriptionResponse> {
  return postJson<SubscriptionResponse>("/v1/subscribers", request, signal);
}

/**
 * Reports a deal by **id** — the value the public deal projection publishes — rather than
 * by slug, so a later slug change does not break the flow. The id is passed through
 * `encodeURIComponent` because it is a response value being placed in a path, and this
 * repo treats a response body as untrusted input like any other.
 */
export function reportDeal(
  dealId: string,
  request: DealReportRequest,
  signal?: AbortSignal,
): Promise<DealReportResponse> {
  return postJson<DealReportResponse>(
    `/v1/deals/${encodeURIComponent(dealId)}/report`,
    request,
    signal,
  );
}
